import React from 'react';
import { Sliders, X } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import { Select } from '../../../../components/ui/Select';
import { AdminOrganization, Plan } from '../../../../types/domain';

interface EditQuotaDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  organization: AdminOrganization;
  plans: Plan[];
  editTier: string;
  editMaxSubmissions: number;
  setEditMaxSubmissions: (val: number) => void;
  editMaxTables: number;
  setEditMaxTables: (val: number) => void;
  editMaxSeats: number;
  setEditMaxSeats: (val: number) => void;
  isUpdatingQuotas: boolean;
  onTierSelect: (tier: string) => void;
  onSaveQuotas: (e: React.FormEvent) => void;
}

export const EditQuotaDrawer: React.FC<EditQuotaDrawerProps> = ({
  isOpen,
  onClose,
  organization,
  plans,
  editTier,
  editMaxSubmissions,
  setEditMaxSubmissions,
  editMaxTables,
  setEditMaxTables,
  editMaxSeats,
  setEditMaxSeats,
  isUpdatingQuotas,
  onTierSelect,
  onSaveQuotas,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-black/60 backdrop-blur-xs flex justify-end font-sans">
      <div className="w-full max-w-md bg-cf-surface border-l border-cf-border h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
        {/* Header */}
        <div className="p-5 border-b border-cf-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sliders size={16} className="text-cf-orange" />
            <h3 className="font-bold text-cf-textPrimary text-sm">
              Edit Quotas: {organization.name}
            </h3>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={onClose}
          >
            <X size={16} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={onSaveQuotas} className="flex-1 overflow-y-auto p-5 space-y-4 text-xs">
          <Select
            label="Subscription Plan Tier"
            size="sm"
            value={editTier}
            onChange={(e) => onTierSelect(e.target.value)}
          >
            {plans.length > 0 ? (
              plans.map((p) => (
                <option key={p.slug} value={p.slug}>
                  {p.name} ({p.slug.toUpperCase()})
                </option>
              ))
            ) : (
              <>
                <option value="free">Free Tier</option>
                <option value="pro">Pro Tier</option>
                <option value="enterprise">Enterprise Tier</option>
              </>
            )}
          </Select>

          <Input
            label="Max Monthly Submissions"
            type="number"
            min="0"
            step="500"
            size="sm"
            value={editMaxSubmissions}
            onChange={(e) => setEditMaxSubmissions(Number(e.target.value))}
          />

          <Input
            label="Max Tables / Datastores"
            type="number"
            min="1"
            size="sm"
            value={editMaxTables}
            onChange={(e) => setEditMaxTables(Number(e.target.value))}
          />

          <Input
            label="Max Team Seats"
            type="number"
            min="1"
            size="sm"
            value={editMaxSeats}
            onChange={(e) => setEditMaxSeats(Number(e.target.value))}
          />

          <div className="pt-4 border-t border-cf-border flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              size="xs"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="xs"
              disabled={isUpdatingQuotas}
              loading={isUpdatingQuotas}
            >
              Save Quotas
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
